/**
 * A panel that slides in from an edge of the screen, built on Radix's
 * dialog; shadcn's wrapper, with its sides' classes from TOOLTIP_CONFIG.
 *
 * A shadcn primitive: kept close to as generated so it can be updated from
 * shadcn.
 *
 * Used by: the plant filter sheet (features/plant-list PlantFilterSheet)
 * only; the app's other bottom panels are shared/components/ui/bottom-panel,
 * built on the dialog.
 *
 * Surprising: its side classes come from TOOLTIP_CONFIG in
 * src/shared/config/tooltipConfig, not from this file, despite the name.
 *
 * @module components/ui/sheet
 */
import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { CloseX } from "@/shared/components/ui/CloseX"
import { TOOLTIP_CONFIG } from "@/shared/config/tooltipConfig"

/** One sheet: open or closed, and what changes that. */
const Sheet = SheetPrimitive.Root

/** A control that opens the sheet. */
const SheetTrigger = SheetPrimitive.Trigger

/** A control that closes the sheet. */
const SheetClose = SheetPrimitive.Close

/** Puts the sheet above the page. */
const SheetPortal = SheetPrimitive.Portal

/** The dimmed page behind the sheet. */
const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-40 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

/** The class names for each side the sheet comes from, and its sizes. */
const sheetVariants = cva(TOOLTIP_CONFIG.variants.sheet.default, {
  variants: {
    side: {
      top: TOOLTIP_CONFIG.variants.sheet.top,
      bottom: TOOLTIP_CONFIG.variants.sheet.bottom,
      left: TOOLTIP_CONFIG.variants.sheet.left,
      right: TOOLTIP_CONFIG.variants.sheet.right,
    },
    size: {
      default: "",
      sm: "",
      lg: "",
    },
  },
  compoundVariants: [
    {
      side: ["top", "bottom"],
      size: "default",
      class: "h-[50%]",
    },
    {
      side: ["top", "bottom"],
      size: "sm",
      class: "h-[30%]",
    },
    {
      side: ["top", "bottom"],
      size: "lg",
      class: "h-[70%]",
    },
    {
      side: ["right", "left"],
      size: "default",
      class: "w-[400px]",
    },
    {
      side: ["right", "left"],
      size: "sm",
      class: "w-[300px]",
    },
    {
      side: ["right", "left"],
      size: "lg",
      class: "w-[500px]",
    },
  ],
  defaultVariants: {
    side: "bottom",
    size: "default",
  },
})

/** Radix's content props, and the side and size. */
export interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

/** The sheet itself, over its overlay, with a close button. */
const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", size = "default", className, children, ...props }, ref): JSX.Element => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(sheetVariants({ side, size }), className)}
      {...props}
    >
      {children}
      <SheetPrimitive.Close asChild>
        <CloseX className="absolute right-4 top-4" />
      </SheetPrimitive.Close>
    </SheetPrimitive.Content>
  </SheetPortal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName

/** The sheet's heading area. */
const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

/** The sheet's buttons. */
const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

/** The sheet's title, announced when it opens. */
const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref): JSX.Element => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

/** The line under the title, read out with it. */
const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref): JSX.Element => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
} 